import { DOCUMENT, Location as NgLocation } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { BehaviorSubject, Subject, filter, switchMap, takeUntil } from 'rxjs';

import { MwAuthService, LocalStorageService, AuthModuleConfigId } from '@core/auth';
import { MwAppConfigService } from '@core/services';

import { MwCoreToolbarService } from 'app/@shared/@components/core-toolbar/core-toolbar.service';
import { MwDialogService } from 'app/@shared/@components/dialog';
import { LanguageViewModel } from './@models';
import { UserProfileDataService } from './user-profile-data.service';
import { UserProfileForm } from './user-profile.form';

@Component({
  selector: 'mw-user-profile',
  templateUrl: 'user-profile.component.html',
  styleUrls: ['./user-profile.component.scss'],
  providers: [UserProfileDataService, UserProfileForm],
})
export class UserProfileComponent implements OnInit, OnDestroy {
  PROFILE_ROUTE = 'user-profile';
  PROFILE_RETURN_URL_KEY = 'mw-user-profile-return-url';

  languages: LanguageViewModel[];
  unlinkInProgress$ = new BehaviorSubject(false);
  linkInProgress$ = new BehaviorSubject(false);
  enableDevincoConnectLinking = false;

  private _unsubscribeAll$ = new Subject<void>();
  private _returnInterval;

  constructor(
    private readonly dataService: UserProfileDataService,
    private readonly coreToolbarService: MwCoreToolbarService,
    private readonly location: NgLocation,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly authService: MwAuthService,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly dialogService: MwDialogService,
    public readonly form: UserProfileForm,
    private config: MwAppConfigService
  ) {}

  ngOnInit(): void {
    this.setupReturnUrl();
    this.dataService.setupData();

    this.setupBackClick();

    this.dataService.languages$
      .pipe(takeUntil(this._unsubscribeAll$))
      .subscribe((languages) => {
        this.languages = languages;
        this.changeDetector.detectChanges();
      });

    this.form.detectUIChanges
      .pipe(takeUntil(this._unsubscribeAll$))
      .subscribe(() => this.changeDetector.detectChanges());

    this.enableDevincoConnectLinking =
      this.config.getInstantConfig().environment.enableDevincoConnectLinking;
  }

  private setupReturnUrl(): void {
    const params = { ...this.route.snapshot.queryParams };
    const returnUrl = params['returnUrl'];

    if (returnUrl) {
      LocalStorageService.saveStoredRedirectRoute(this.PROFILE_ROUTE, true);
      this._returnInterval = setInterval(() => {
        this.storeReturnUrl(returnUrl);
      }, 29000);
      this.storeReturnUrl(returnUrl);
      delete params.returnUrl;
      this.router.navigate([], { queryParams: params });
    }
  }

  private storeReturnUrl(returnUrl: any): void {
    LocalStorageService.setItemWithExpiry(
      this.PROFILE_RETURN_URL_KEY,
      returnUrl,
      60
    );
  }

  private setupBackClick(): void {
    this.coreToolbarService.backClick$
      .pipe(takeUntil(this._unsubscribeAll$))
      .subscribe(() => {
        const retUrl = LocalStorageService.getItemWithExpiry(
          this.PROFILE_RETURN_URL_KEY
        );
        if (retUrl) {
          LocalStorageService.removeItem(this.PROFILE_RETURN_URL_KEY);
          LocalStorageService.deleteStoredRedirectRoute();
          window.location.href = retUrl;
        } else {
          this.location.back();
        }
      });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll$.next();
    this._unsubscribeAll$.complete();
    if (this._returnInterval) {
      clearInterval(this._returnInterval);
    }
  }

  onLogoff(): void {
    LocalStorageService.saveStoredRedirectRoute(this.PROFILE_ROUTE);
    this.authService.logoff(this.form.linkWithConfigId).subscribe();
  }

  onLogin(): void {
    this.authService.authorize(this.PROFILE_ROUTE, this.form.linkWithConfigId);
  }

  onLogout(): void {
    this.authService.logout();
  }

  onLinkAccount(): void {
    this.linkInProgress$.next(true);
    this.dataService
      .link()
      .pipe(switchMap(() => this.form.refreshUserLoginsData()))
      .subscribe(() => {
        this.linkInProgress$.next(false);
        if (this.form.linkWithConfigId === AuthModuleConfigId.DevincoConnect) {
          this.changeDetector.detectChanges();
        } else {
          LocalStorageService.setLastActiveCompanyId(
            this.form.selectedCompanyId
          );
          LocalStorageService.saveStoredRedirectRoute(this.PROFILE_ROUTE);
          this.document.location.href = this.document.location.origin;
        }
      });
  }

  onUnlinkAccount(): void {
    this.dialogService
      .confirm()
      .afterClosed()
      .pipe(filter((result) => result === true))
      .subscribe(() => {
        this.unlinkInProgress$.next(true);
        this.dataService
          .unlink()
          .pipe(switchMap(() => this.form.refreshUserLoginsData()))
          .subscribe(() => {
            this.unlinkInProgress$.next(false);

            if (
              this.form.linkWithConfigId === AuthModuleConfigId.DevincoConnect
            ) {
              this.changeDetector.detectChanges();
            } else {
              this.document.location.reload();
            }
          });
      });
  }

  onSave(): void {
    if (this.form.invalid) {
      return;
    }
    const dto = this.form.toDto();
    this.dataService.save(dto);
  }
}
